import React from 'react';
import { NavLink } from 'react-router-dom';
import { FiArrowRight, FiBarChart2, FiCheckCircle, FiClock, FiExternalLink, FiShield, FiZap } from 'react-icons/fi';
import { Seo } from '../components/Seo';
import { site } from '../config/site';
import { offers } from '../data/offers';
import { MiniBarChart } from '../components/charts/MiniBarChart';
import { useInViewOnce } from '../hooks/useInViewOnce';
import { Icon } from '../components/ui/Icon';

function formatEur(n: number) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}

export default function HomePage() {
  const [picked, setPicked] = React.useState(offers[0]?.slug ?? 'netbet');
  const [deposit, setDeposit] = React.useState(25);
  const pickedOffer = offers.find((o) => o.slug === picked) ?? offers[0];

  const bonusMax = pickedOffer?.welcome.maxEur ?? 0;
  const estimatedBonus = Math.min(bonusMax, deposit); // deliberately simple (player-first)

  const [odd, setOdd] = React.useState('2.10');
  const oddNumber = Number(String(odd).replace(',', '.'));
  const implied = oddNumber > 1 ? 100 / oddNumber : 0;

  const heroAnchors = [
    { id: 'offres', label: 'Top offres' },
    { id: 'comparateur', label: 'Comparateur express' },
    { id: 'simulateur', label: 'Simulateur de cotes' },
    { id: 'paiements', label: 'Retraits & paiements' },
    { id: 'avis', label: 'Avis & profils' },
    { id: 'faq', label: 'FAQ' },
  ];

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: site.name,
      url: site.url,
      inLanguage: 'fr-FR',
      description:
        'Comparateur d’offres de paris sportifs en France : bonus, cotes, retraits et conditions, avec avis par opérateur.',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: site.name,
      url: site.url,
      logo: `${site.url}/images/logo.webp`,
    },
  ];

  return (
    <>
      <Seo
        title="Offres de paris sportifs en France"
        description="Bonus, cotes, retraits, conditions: LuckyBetFR compare les offres de paris sportifs en France avec des avis clairs et des outils rapides."
        canonicalPath="/"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-radial-fade-2 opacity-60" />
        <div className="lbf-container relative py-10 sm:py-16 lg:py-20">
          <div className="grid gap-10 lg:grid-cols-12 lg:items-center items-center justify-center">
            <div className="lg:col-span-6">
              <div className="flex justify-center lg:justify-start">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-white/90">
                <Icon icon={FiZap} aria-hidden="true" />
                Comparateur rapide • France • 18+
                </div>
              </div>

              <h1 className="mt-5 text-center text-3xl font-black tracking-tight text-white sm:text-5xl lg:text-left">
                Offres de paris sportifs en France.
                <span className="block bg-gradient-to-r from-fuchsia-400 to-indigo-300 bg-clip-text text-transparent">
                  Conditions claires, choix plus tranché.
                </span>
              </h1>
              <p className="mt-5 mx-auto max-w-xl text-center text-base text-zinc-200/85 sm:text-lg lg:mx-0 lg:text-left">
                Bonus de bienvenue, vitesse de retrait, qualité des cotes: on te donne la vue d’ensemble, puis le détail
                par opérateur. Tu cliques, tu sais.
              </p>

              <div className="mt-7 mx-auto flex w-full max-w-sm flex-col gap-3 sm:mx-0 sm:max-w-none sm:flex-row sm:items-center sm:justify-center lg:justify-start">
                <NavLink to="/comparatif-offres" className="lbf-btn-primary w-full sm:w-auto">
                  Comparer maintenant <Icon icon={FiArrowRight} aria-hidden="true" />
                </NavLink>
                <NavLink to="/offres" className="lbf-btn-ghost w-full sm:w-auto">
                  Voir les avis
                </NavLink>
              </div>

              {/* Desktop feature cards */}
              <div className="mt-8 hidden gap-2 sm:grid sm:grid-cols-2">
                <div className="lbf-card p-4">
                  <div className="flex items-center gap-2 text-sm font-extrabold text-white">
                    <Icon icon={FiShield} aria-hidden="true" />
                    Focus confiance
                  </div>
                  <p className="mt-2 text-sm text-zinc-200/80">
                    On parle retraits, KYC, conditions — pas juste “bonus + gros bouton”.
                  </p>
                </div>
                <div className="lbf-card p-4">
                  <div className="flex items-center gap-2 text-sm font-extrabold text-white">
                    <Icon icon={FiClock} aria-hidden="true" />
                    Gain de temps
                  </div>
                  <p className="mt-2 text-sm text-zinc-200/80">
                    Comparateur express + pages détaillées: tu passes du “peut-être” au “ok”.
                  </p>
                </div>
              </div>

              {/* Mobile: compact value chips */}
              <div className="mt-6 mx-auto flex w-full max-w-md flex-wrap justify-center gap-2 sm:hidden">
                <span className="lbf-badge">Retraits & conditions</span>
                <span className="lbf-badge">Comparaison rapide</span>
                <span className="lbf-badge">18+ • France</span>
              </div>

              {/* Hero anchors: grid on mobile, wrap on larger screens */}
              <div className="mt-8 mx-auto grid w-full max-w-md grid-cols-2 gap-2 sm:mx-0 sm:max-w-none sm:flex sm:flex-wrap sm:justify-center lg:justify-start">
                {heroAnchors.map((a) => (
                  <a
                    key={a.id}
                    href={`#${a.id}`}
                    className="lbf-btn-ghost w-full justify-center px-3 py-2 text-xs sm:w-auto"
                  >
                    {a.label}
                  </a>
                ))}
              </div>
            </div>

            <div className="relative isolate lg:col-span-6">
              {/* Decorative hero images (kept behind the content for readability) */}
              <img
                src="/images/heroleft.webp"
                alt=""
                aria-hidden="true"
                width={520}
                height={520}
                className="pointer-events-none select-none absolute -left-28 top-[-60px] hidden w-[320px] rounded-2xl lg:block"
                decoding="async"
              />
              <img
                src="/images/heroright.webp"
                alt=""
                aria-hidden="true"
                width={520}
                height={520}
                className="pointer-events-none select-none absolute -right-24 bottom-[-90px] hidden w-[300px] rounded-2xl lg:block"
                decoding="async"
              />

              <div className="relative z-10 mx-auto w-full max-w-lg">
                <div className="absolute -inset-4 rounded-[32px] bg-gradient-to-r from-fuchsia-500/20 to-indigo-500/20 blur-2xl" />
                <div className="relative overflow-hidden rounded-3xl border border-white/12 bg-zinc-950/70 p-5 shadow-glow backdrop-blur">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-extrabold text-white">Top du moment</div>
                    <div className="lbf-badge animate-shimmer bg-[length:220%_220%] bg-gradient-to-r from-white/5 via-white/10 to-white/5">
                      Mise à jour 2025
                    </div>
                  </div>
                  <div className="mt-4 grid gap-3">
                    {offers.slice(0, 3).map((o) => (
                      <div
                        key={o.slug}
                        className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 transition hover:bg-white/10"
                      >
                        <NavLink to={`/offres/${o.slug}`} className="group flex min-w-0 flex-1 items-center gap-3">
                          <img
                            src={o.imagePath}
                            alt={o.brand}
                            className="h-10 w-10 rounded-xl border border-white/10 bg-white/5 object-contain p-1"
                            loading="lazy"
                            decoding="async"
                          />
                          <div className="min-w-0">
                            <div className="truncate text-sm font-extrabold text-white">{o.brand}</div>
                            <div className="truncate text-xs text-zinc-300/80">{o.welcome.headline}</div>
                          </div>
                          <div className="ml-auto hidden text-xs font-semibold text-white/80 group-hover:text-white sm:block">
                            Avis <Icon icon={FiArrowRight} className="ml-1 inline" aria-hidden="true" />
                          </div>
                        </NavLink>

                        {o.links.official ? (
                          <a
                            href={o.links.official}
                            target="_blank"
                            rel="noreferrer"
                            className="lbf-btn-ghost px-3 py-2 text-xs"
                            aria-label={`Site officiel ${o.brand}`}
                            title={`Site officiel ${o.brand}`}
                          >
                            <Icon icon={FiExternalLink} aria-hidden="true" />
                          </a>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OFFERS */}
      <SectionOffers />
      <SectionQuickComparator
        picked={picked}
        setPicked={setPicked}
        deposit={deposit}
        setDeposit={setDeposit}
        estimatedBonus={estimatedBonus}
        pickedOfferName={pickedOffer?.brand ?? '—'}
      />
      <SectionOdds odd={odd} setOdd={setOdd} implied={implied} />
      <SectionPayments />
      <SectionPersonas />
      <SectionFaqTeaser />
    </>
  );
}

function SectionOffers() {
  const { ref, inView } = useInViewOnce<HTMLDivElement>();
  return (
    <section id="offres" className="lbf-container py-14 sm:py-18">
      <div
        ref={ref}
        className={[
          'transition-all duration-700',
          inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
        ].join(' ')}
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-2xl font-black text-white sm:text-3xl">Top offres (avis + détails)</h2>
            <p className="mt-2 max-w-2xl text-sm text-zinc-200/80">
              Trois profils, trois styles. Clique sur une marque et tu as une page complète: bonus, conditions, retraits
              et graphiques.
            </p>
          </div>
          <NavLink to="/offres" className="lbf-btn-ghost">
            Tout voir <Icon icon={FiArrowRight} aria-hidden="true" />
          </NavLink>
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {offers.map((o) => (
            <div key={o.slug} className="lbf-card overflow-hidden p-5 transition hover:bg-white/10">
              <NavLink to={`/offres/${o.slug}`} className="group block">
                <div className="flex items-center gap-3">
                  <img
                    src={o.imagePath}
                    alt={o.brand}
                    className="h-12 w-12 rounded-2xl border border-white/10 bg-white/5 object-contain p-1"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="min-w-0">
                    <div className="truncate text-base font-extrabold text-white">{o.brand}</div>
                    <div className="truncate text-xs text-zinc-300/80">{o.tagline}</div>
                  </div>
                </div>
                <p className="mt-4 text-sm text-zinc-200/85">{o.heroPitch}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="lbf-badge">
                    <Icon icon={FiCheckCircle} aria-hidden="true" />
                    {o.welcome.headline}
                  </span>
                  <span className="lbf-badge">
                    <Icon icon={FiClock} aria-hidden="true" />
                    Retrait typique: ~{o.payouts.typicalHours}h
                  </span>
                </div>
              </NavLink>

              <div className="mt-5 flex flex-col gap-2 sm:flex-row">
                <NavLink to={`/offres/${o.slug}`} className="lbf-btn-primary">
                  Lire l’avis <Icon icon={FiArrowRight} aria-hidden="true" />
                </NavLink>
                {o.links.official ? (
                  <a href={o.links.official} target="_blank" rel="noreferrer" className="lbf-btn-ghost">
                    Site officiel <Icon icon={FiExternalLink} aria-hidden="true" />
                  </a>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SectionQuickComparator(props: {
  picked: string;
  setPicked: (v: string) => void;
  deposit: number;
  setDeposit: (n: number) => void;
  estimatedBonus: number;
  pickedOfferName: string;
}) {
  const { ref, inView } = useInViewOnce<HTMLDivElement>();
  return (
    <section id="comparateur" className="lbf-container py-14 sm:py-18">
      <div
        ref={ref}
        className={[
          'grid gap-6 lg:grid-cols-12 transition-all duration-700',
          inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
        ].join(' ')}
      >
        <div className="lg:col-span-5">
          <h2 className="text-2xl font-black text-white sm:text-3xl">Comparateur express</h2>
          <p className="mt-3 text-sm text-zinc-200/80">
            Tu veux juste une réponse rapide? Choisis une marque, un dépôt, et on te donne un ordre d’idée du bonus
            “utile” — sans te vendre du rêve.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <span className="lbf-badge">Lecture simple</span>
            <span className="lbf-badge">Focus conditions</span>
            <span className="lbf-badge">Sans surcharge</span>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="lbf-card p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="grid gap-2 text-sm">
                <span className="font-semibold text-white/90">Marque</span>
                <select
                  value={props.picked}
                  onChange={(e) => props.setPicked(e.target.value)}
                  className="rounded-xl border border-white/10 bg-zinc-950 px-3 py-2 text-white outline-none focus:ring-2 focus:ring-fuchsia-400/60"
                >
                  {offers.map((o) => (
                    <option key={o.slug} value={o.slug}>
                      {o.brand}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-2 text-sm">
                <span className="font-semibold text-white/90">Dépôt</span>
                <input
                  type="range"
                  min={10}
                  max={100}
                  step={5}
                  value={props.deposit}
                  onChange={(e) => props.setDeposit(Number(e.target.value))}
                />
                <div className="text-xs text-zinc-300/80">Montant: {formatEur(props.deposit)}</div>
              </label>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-xs font-semibold text-zinc-300/80">Bonus estimé (ordre d’idée)</div>
                <div className="mt-2 text-2xl font-black text-white">{formatEur(props.estimatedBonus)}</div>
                <div className="mt-2 text-xs text-zinc-300/80">
                  Basé sur un modèle simple. Vérifie toujours les conditions officielles avant dépôt.
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-xs font-semibold text-zinc-300/80">Prochain clic</div>
                <div className="mt-2 text-sm font-bold text-white">{props.pickedOfferName}</div>
                <NavLink to={`/offres/${props.picked}`} className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-white/85 hover:text-white">
                  Lire l’avis complet <Icon icon={FiArrowRight} aria-hidden="true" />
                </NavLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionOdds(props: { odd: string; setOdd: (v: string) => void; implied: number }) {
  const { ref, inView } = useInViewOnce<HTMLDivElement>();
  const impliedClean = Number.isFinite(props.implied) ? Math.max(0, Math.min(100, props.implied)) : 0;

  return (
    <section id="simulateur" className="lbf-container py-14 sm:py-18">
      <div
        ref={ref}
        className={[
          'grid gap-6 lg:grid-cols-12 transition-all duration-700',
          inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
        ].join(' ')}
      >
        <div className="lg:col-span-5">
          <h2 className="text-2xl font-black text-white sm:text-3xl">Simulateur de cotes</h2>
          <p className="mt-3 text-sm text-zinc-200/80">
            Une cote n’est pas un “feeling”, c’est une proba déguisée. Entre la sensation et la réalité, il y a un
            chiffre.
          </p>
          <div className="mt-6 lbf-card p-5">
            <label className="grid gap-2 text-sm">
              <span className="font-semibold text-white/90">Cote décimale</span>
              <input
                inputMode="decimal"
                value={props.odd}
                onChange={(e) => props.setOdd(e.target.value)}
                className="rounded-xl border border-white/10 bg-zinc-950 px-3 py-2 text-white outline-none focus:ring-2 focus:ring-fuchsia-400/60"
                placeholder="Ex: 2.10"
              />
            </label>
            <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="text-xs font-semibold text-zinc-300/80">Probabilité implicite</div>
              <div className="mt-2 text-2xl font-black text-white">{impliedClean.toFixed(1)}%</div>
              <div className="mt-2 text-xs text-zinc-300/80">
                Plus c’est bas, plus le scénario est “rare” selon la cote.
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <MiniBarChart
            title="Qualité des cotes (tendance)"
            hint="Score indicatif par sport (comparaison rapide)."
            items={[
              { label: 'Foot', score: 86 },
              { label: 'Tennis', score: 83 },
              { label: 'Rugby', score: 80 },
              { label: 'Basket', score: 79 },
              { label: 'Turf', score: 85 },
            ]}
          />
          <div className="mt-4 lbf-card p-5">
            <div className="flex items-center gap-2 text-sm font-extrabold text-white">
              <Icon icon={FiBarChart2} aria-hidden="true" />
              Lecture rapide
            </div>
            <ul className="mt-3 grid gap-2 text-sm text-zinc-200/85">
              <li>
                - Une cote de <span className="font-bold text-white">2.00</span> ≈ <span className="font-bold text-white">50%</span>.
              </li>
              <li>
                - Une cote de <span className="font-bold text-white">1.50</span> ≈ <span className="font-bold text-white">66.7%</span>.
              </li>
              <li>
                - Une cote de <span className="font-bold text-white">3.00</span> ≈ <span className="font-bold text-white">33.3%</span>.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionPayments() {
  const { ref, inView } = useInViewOnce<HTMLDivElement>();
  const methods = [
    {
      title: 'Carte bancaire',
      note: 'Rapide pour déposer, retrait variable selon validation.',
    },
    {
      title: 'Virement',
      note: 'Souvent plus lent, mais très “standard”.',
    },
    {
      title: 'Portefeuille en ligne',
      note: 'Quand dispo, c’est souvent le bon compromis vitesse / simplicité.',
    },
  ];

  const [active, setActive] = React.useState(0);

  return (
    <section id="paiements" className="lbf-container py-14 sm:py-18">
      <div
        ref={ref}
        className={[
          'grid gap-6 lg:grid-cols-12 transition-all duration-700',
          inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
        ].join(' ')}
      >
        <div className="lg:col-span-5">
          <h2 className="text-2xl font-black text-white sm:text-3xl">Retraits & paiements</h2>
          <p className="mt-3 text-sm text-zinc-200/80">
            Le bonus impressionne, le retrait décide. Les meilleurs opérateurs rendent le cash-out clair, et ton dossier
            propre accélère tout.
          </p>
          <div className="mt-6 grid gap-2">
            {methods.map((m, idx) => (
              <button
                key={m.title}
                type="button"
                onClick={() => setActive(idx)}
                className={[
                  'text-left rounded-2xl border px-4 py-3 transition',
                  idx === active ? 'border-white/15 bg-white/10' : 'border-white/10 bg-white/5 hover:bg-white/10',
                ].join(' ')}
              >
                <div className="text-sm font-extrabold text-white">{m.title}</div>
                <div className="mt-1 text-xs text-zinc-300/80">{m.note}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="lbf-card p-5">
            <div className="text-sm font-extrabold text-white">Ce qui accélère un retrait</div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {[
                { t: 'KYC fait tôt', d: 'Pièce + justificatif validés = moins de surprises.' },
                { t: 'Méthode cohérente', d: 'Dépose et retire via la même voie quand possible.' },
                { t: 'Budget net', d: 'Évite 10 micro-retraits: ça rallonge souvent.' },
                { t: 'Conditions lues', d: 'Certains bonus imposent un turnover: anticipe.' },
              ].map((x) => (
                <div key={x.t} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="text-sm font-bold text-white/90">{x.t}</div>
                  <div className="mt-2 text-xs text-zinc-300/80">{x.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionPersonas() {
  const { ref, inView } = useInViewOnce<HTMLDivElement>();
  return (
    <section id="avis" className="lbf-container py-14 sm:py-18">
      <div
        ref={ref}
        className={[
          'grid gap-6 lg:grid-cols-12 transition-all duration-700',
          inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
        ].join(' ')}
      >
        <div className="lg:col-span-5">
          <h2 className="text-2xl font-black text-white sm:text-3xl">Avis par profil</h2>
          <p className="mt-3 text-sm text-zinc-200/80">
            Pas besoin d’être le même joueur tous les jours. Ici, on te propose des choix par style — pas par slogans.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <span className="lbf-badge">Débutant</span>
            <span className="lbf-badge">Régulier</span>
            <span className="lbf-badge">Turf</span>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="grid gap-4 md:grid-cols-3">
            <PersonaCard title="Débutant" pick="NetBet" note="Interface claire, démarrage sans stress." />
            <PersonaCard title="Régulier" pick="Genybet" note="Promos sur les temps forts, profondeur." />
            <PersonaCard title="Turf" pick="ZEturf" note="Ciblé courses, navigation efficace." />
          </div>
          <div className="mt-4 lbf-card p-5">
            <div className="text-sm font-extrabold text-white">Mini-check avant de cliquer</div>
            <ul className="mt-3 grid gap-2 text-sm text-zinc-200/85">
              <li>- Budget: fixe-le, et tiens-le.</li>
              <li>- Retrait: regarde la vitesse + les méthodes.</li>
              <li>- Bonus: vérifie les conditions de mise (turnover).</li>
              <li>- Emotion: si tu joues “pour te refaire”, stop.</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function PersonaCard(props: { title: string; pick: string; note: string }) {
  return (
    <div className="lbf-card p-5">
      <div className="text-xs font-bold uppercase tracking-wider text-zinc-300/70">{props.title}</div>
      <div className="mt-2 text-lg font-black text-white">{props.pick}</div>
      <div className="mt-2 text-sm text-zinc-200/80">{props.note}</div>
    </div>
  );
}

function SectionFaqTeaser() {
  const { ref, inView } = useInViewOnce<HTMLDivElement>();
  const items = [
    { q: 'Pourquoi les offres “changent” souvent ?', a: 'Parce que les promos suivent le calendrier sportif et les budgets marketing.' },
    { q: 'Une cote plus haute = meilleur choix ?', a: 'Pas toujours: regarde aussi la proba réelle, la marge et tes limites.' },
    { q: 'Combien de temps pour retirer ?', a: 'Ça dépend KYC + méthode. D’où notre focus “retraits” sur chaque avis.' },
  ];

  return (
    <section id="faq" className="lbf-container py-14 sm:py-18">
      <div
        ref={ref}
        className={[
          'grid gap-6 lg:grid-cols-12 transition-all duration-700',
          inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
        ].join(' ')}
      >
        <div className="lg:col-span-5">
          <h2 className="text-2xl font-black text-white sm:text-3xl">FAQ rapide</h2>
          <p className="mt-3 text-sm text-zinc-200/80">
            Tu veux une réponse courte avant de plonger dans les détails? Voilà les trois questions qui reviennent tout
            le temps.
          </p>
          <NavLink to="/faq" className="lbf-btn-primary mt-6">
            Voir toute la FAQ <Icon icon={FiArrowRight} aria-hidden="true" />
          </NavLink>
        </div>

        <div className="lg:col-span-7">
          <div className="grid gap-3">
            {items.map((x) => (
              <div key={x.q} className="lbf-card p-5">
                <div className="text-sm font-extrabold text-white">{x.q}</div>
                <div className="mt-2 text-sm text-zinc-200/85">{x.a}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}


